import React, { useEffect, useRef, useState } from 'react';
import { apiFetch } from '@/utils/api';

export const EMOJI_MAP: Record<string, string> = {
  'ch1': '/emojis/5361767787129766930.json',
  '1': '/emojis/5406931687529552683.json',
  '87': '/emojis/5307564543939997872.json',
  '91': '/emojis/5404534885324988233.json',
  '98': '/emojis/5307793564481126555.json',
  '101': '/emojis/5307830071703145664.json',
  '104': '/emojis/5967522716062847679.json',
  '105': '/emojis/5404534743591065435.json',
  '106': '/emojis/5400182197963496946.json',
  '107': '/emojis/5404414183859069853.json',
  '108': '/emojis/5409297334041356605.json',
  '109': '/emojis/5402287629586702733.json',
  '110': '/emojis/5402287878694802880.json',
  '111': '/emojis/5258332798409783582.json',
  '112': '/emojis/5247144550337651418.json',
  // Community / marketing links (see server/config/bot_emojis.json)
  'Numeo8': '/emojis/5312161417372142817.json',
  '41': '/emojis/5229027828527309057.json',
  '80': '/emojis/5406931687529552683.json',
  '92': '/emojis/5406571674780863226.json',
};

// Memory cache for dynamic admin emojis fetched from backend
let DYNAMIC_ADMIN_EMOJIS: Record<string, any> | null = null;
let DYNAMIC_FETCH_PROMISE: Promise<any> | null = null;

const fetchAdminEmojiDetails = async (): Promise<Record<string, any>> => {
  if (DYNAMIC_ADMIN_EMOJIS) return DYNAMIC_ADMIN_EMOJIS;
  if (DYNAMIC_FETCH_PROMISE) return DYNAMIC_FETCH_PROMISE;

  DYNAMIC_FETCH_PROMISE = (async () => {
    try {
      const res = await apiFetch('/api/admin/emojis');
      const data = await res.json();
      if (data.success && data.emojiDetails) {
        DYNAMIC_ADMIN_EMOJIS = data.emojiDetails;
        return data.emojiDetails;
      }
    } catch (e) {}
    DYNAMIC_ADMIN_EMOJIS = {};
    return {};
  })();

  return DYNAMIC_FETCH_PROMISE;
};

// Global memory cache for custom emoji animation JSON data to guarantee 0ms instant load and 60 FPS
const GLOBAL_EMOJI_CACHE: Record<string, any> = {};

let globalLottiePromise: Promise<any> | null = null;

const getLottieInstance = (): Promise<any> => {
  if ((window as any).lottie) return Promise.resolve((window as any).lottie);
  if (globalLottiePromise) return globalLottiePromise;

  globalLottiePromise = new Promise((resolve, reject) => {
    const existing = document.getElementById('lottie-script') as HTMLScriptElement;
    if (existing) {
      if ((window as any).lottie) return resolve((window as any).lottie);
      const checkInterval = setInterval(() => {
        if ((window as any).lottie) {
          clearInterval(checkInterval);
          resolve((window as any).lottie);
        }
      }, 50);
      existing.addEventListener('load', () => {
        clearInterval(checkInterval);
        resolve((window as any).lottie);
      });
      existing.addEventListener('error', (e) => {
        clearInterval(checkInterval);
        globalLottiePromise = null;
        reject(e);
      });
      return;
    }

    const script = document.createElement('script');
    script.id = 'lottie-script';
    script.src = 'https://cdnjs.cloudflare.com/ajax/libs/bodymovin/5.12.2/lottie.min.js';
    script.onload = () => resolve((window as any).lottie);
    script.onerror = (e) => {
      globalLottiePromise = null;
      reject(e);
    };
    document.head.appendChild(script);
  });

  return globalLottiePromise;
};

interface CustomEmojiPlayerProps {
  emojiId: string;
  size?: number;
  className?: string;
  glowColor?: string;
}

export const CustomEmojiPlayer: React.FC<CustomEmojiPlayerProps> = ({
  emojiId,
  size = 80,
  className = "",
  glowColor,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const animInstance = useRef<any>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let isMounted = true;

    async function init() {
      try {
        const cleanId = String(emojiId).replace(/^#/, '').replace(/^Emoji\s*#?/i, '').trim();
        let url = EMOJI_MAP[emojiId] || EMOJI_MAP[cleanId] || EMOJI_MAP[`#${cleanId}`];

        // Fallback: check dynamic admin emojis if not in static map
        if (!url || (!EMOJI_MAP[emojiId] && !EMOJI_MAP[cleanId])) {
          const details = await fetchAdminEmojiDetails();
          const item =
            details[emojiId] ||
            details[cleanId] ||
            details[`#${cleanId}`] ||
            details[`Emoji #${cleanId}`] ||
            details[`Emoji ${cleanId}`];
          if (item?.fileUrl) {
            url = item.fileUrl;
          }
        }
        if (!url) url = EMOJI_MAP['ch1'];

        let animationData = GLOBAL_EMOJI_CACHE[url];

        if (!animationData) {
          const res = await fetch(url);
          if (res.ok) {
            if (url.endsWith('.tgs')) {
              try {
                const buffer = await res.arrayBuffer();
                const ds = new DecompressionStream('gzip');
                const writer = ds.writable.getWriter();
                writer.write(buffer);
                writer.close();
                const decompressed = await new Response(ds.readable).arrayBuffer();
                const jsonStr = new TextDecoder().decode(decompressed);
                animationData = JSON.parse(jsonStr);
              } catch (_) {}
            } else {
              try {
                const text = await res.text();
                if (text.startsWith('{')) {
                  animationData = JSON.parse(text);
                } else {
                  const buffer = await res.arrayBuffer();
                  const ds = new DecompressionStream('gzip');
                  const writer = ds.writable.getWriter();
                  writer.write(buffer);
                  writer.close();
                  const decompressed = await new Response(ds.readable).arrayBuffer();
                  const jsonStr = new TextDecoder().decode(decompressed);
                  animationData = JSON.parse(jsonStr);
                }
              } catch (_) {}
            }
            if (animationData) {
              GLOBAL_EMOJI_CACHE[url] = animationData;
            }
          }
        }

        if (!animationData) return;

        const lottie = await getLottieInstance();
        if (!isMounted || !containerRef.current) return;

        if (animInstance.current) {
          animInstance.current.destroy();
        }

        if (containerRef.current && isMounted) {
          containerRef.current.innerHTML = '';
          animInstance.current = lottie.loadAnimation({
            container: containerRef.current,
            renderer: 'svg',
            loop: true,
            autoplay: true,
            animationData: animationData,
            rendererSettings: {
              preserveAspectRatio: 'xMidYMid meet',
            },
          });

          if (isMounted) setLoaded(true);

          // WebKit 0px height flexbox fix
          setTimeout(() => {
            if (containerRef.current) {
              const svg = containerRef.current.querySelector('svg');
              if (svg) {
                svg.style.width = '100%';
                svg.style.height = '100%';
                svg.style.display = 'block';
              }
            }
          }, 30);
        }
      } catch (e) {
        console.warn('[CustomEmojiPlayer] Error initializing:', e);
      }
    }

    init();

    return () => {
      isMounted = false;
      if (animInstance.current) {
        animInstance.current.destroy();
      }
    };
  }, [emojiId]);

  return (
    <div
      className={`relative inline-flex items-center justify-center pointer-events-none select-none ${className}`}
      style={{ width: size, height: size }}
    >
      {glowColor && (
        <div
          className="absolute inset-0 rounded-full blur-2xl opacity-40 animate-pulse pointer-events-none"
          style={{ backgroundColor: glowColor }}
        />
      )}
      <div
        ref={containerRef}
        className="w-full h-full relative z-10 flex items-center justify-center [&>svg]:w-full [&>svg]:h-full [&>svg]:block"
      />
    </div>
  );
};

export default CustomEmojiPlayer;


